import type { ChangelogRouteProps } from './Changelog';
import type { CustomPageRouteProps } from './CustomPage';
import type { DocRouteProps } from './Doc';
import type { HomeRouteProps } from './Home';
import type { ReferenceRouteProps } from './Reference';
import type { HubResponseProps } from '@readme/iso';

import loadable from '@loadable/component';
import React from 'react';
import { Route, Switch } from 'react-router-dom';

import { InitializeSuperHubSidebar } from '@core/store';

import RouteLoadingFallback from '@ui/RouteLoadingFallback';

import { SuperHubRoutePaths } from './types';

/**
 * Route components are imported via loadable to allow for
 * code-splitting and lazy loading of the components.
 * This is especially important for the SuperHub routes
 * as they can be large and complex, and we want to avoid
 * loading them all at once.
 */
const GraphQLPage = loadable(() => import('@routes/GraphQL'), { ssr: false, fallback: <RouteLoadingFallback /> });
const OAuthRedirect = loadable(() => import('@routes/Reference/OAuthRedirect'), {
  ssr: true,
  fallback: <RouteLoadingFallback />,
});
const PageNotFound = loadable(() => import('@routes/PageNotFound'), { ssr: true, fallback: <RouteLoadingFallback /> });
const SuperHubChangelog = loadable(() => import('./Changelog'), { ssr: true, fallback: <RouteLoadingFallback /> });
const SuperHubCustomPage = loadable(() => import('./CustomPage'), { ssr: true, fallback: <RouteLoadingFallback /> });
const SuperHubDiscussions = loadable(() => import('./Discussions'), { ssr: true, fallback: <RouteLoadingFallback /> });
const SuperHubDoc = loadable(() => import('./Doc'), { ssr: true, fallback: <RouteLoadingFallback /> });
const SuperHubHome = loadable(() => import('./Home'), { ssr: true, fallback: <RouteLoadingFallback /> });
const SuperHubRecipes = loadable(() => import('./Recipes'), { ssr: true, fallback: <RouteLoadingFallback /> });
const SuperHubReference = loadable(() => import('./Reference'), { ssr: true, fallback: <RouteLoadingFallback /> });

type RoutePropsType = HubResponseProps<
  ChangelogRouteProps & CustomPageRouteProps & DocRouteProps & HomeRouteProps & ReferenceRouteProps
>;

/**
 * Top-level browser router for SuperHub.
 */
export default function SuperHubRoutes(props: RoutePropsType) {
  const { sidebar: initialSidebar } = props;

  return (
    <InitializeSuperHubSidebar sidebar={initialSidebar}>
      <Switch>
        {/**
         * Routes for the Hub's view mode.
         * Edit mode routes have an outlet in the `EditContainer` component.
         */}
        <Route exact path="/">
          <SuperHubHome {...props} />
        </Route>
        <Route path={SuperHubRoutePaths.viewGuidePage}>
          <SuperHubDoc {...props} />
        </Route>
        <Route path={SuperHubRoutePaths.viewReferencePage}>
          <SuperHubReference {...props} />
        </Route>
        <Route path={SuperHubRoutePaths.viewCustomPage}>
          <SuperHubCustomPage {...props} />
        </Route>
        <Route path={SuperHubRoutePaths.viewChangelog}>
          <SuperHubChangelog {...props} />
        </Route>
        <Route path={SuperHubRoutePaths.viewRecipes}>
          <SuperHubRecipes {...props} />
        </Route>
        <Route path={SuperHubRoutePaths.viewDiscussions}>
          <SuperHubDiscussions {...props} />
        </Route>
        <Route path={SuperHubRoutePaths.viewGraphQL}>
          <GraphQLPage />
        </Route>
        <Route path={SuperHubRoutePaths.oauth2Redirect}>
          <OAuthRedirect />
        </Route>
        <Route>
          <PageNotFound {...props} />
        </Route>
      </Switch>
    </InitializeSuperHubSidebar>
  );
}
